/**
 * AdminJobRunDetailPage - View a single job run and its items
 *
 * Drives:
 * - GET /api/admin/jobs/:id
 * - GET /api/admin/jobs/:id/items
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  AlertTriangle,
  ChevronLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  Download,
  Filter,
  Wrench,
  X,
  ClipboardCheck,
  ExternalLink,
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../lib/api';

// Allowed reviewer models (must match backend ALLOWED_REVIEWER_MODELS)
const ALLOWED_REVIEWER_MODELS = [
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Recommended)' },
  { value: 'claude-opus-4-20250514', label: 'Claude Opus 4' },
];

// Allowed tagging models for fix-failed operations
const ALLOWED_TAGGING_MODELS = [
  { value: '', label: 'Use original model' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Recommended)' },
  { value: 'claude-opus-4-20250514', label: 'Claude Opus 4 (Higher quality)' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (Faster/cheaper)' },
];

// Review status type
type ReviewStatus = 'none' | 'pending' | 'changes_suggested' | 'approved' | 'applied' | 'no_changes';

// Latest review info attached to job - must match backend getReviewStatusForJob() shape
interface LatestReview {
  id: string;
  finished_at: string | null;
  items_attempted: number;
  items_suggested: number;
  approved_at: string | null;
  applied_at: string | null;
  created_at: string;
}

// Backend response shape from worker/lib/admin-config.js handleGetJobRunDetail
interface JobRun {
  id: string;
  job_type: string; // ai_facets_v1 | ai_overlay_genres_v1 | ai_microtag_v1
  model: string;
  prompt_version: string;
  selector: Record<string, unknown>;
  error_summary: string | null;
  items_attempted: number;
  items_succeeded: number;
  items_failed: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  started_at: string;
  finished_at: string | null;
  // Backend extracts these for ai_microtag_v1 jobs
  tag_key?: string | null;
  tag_prompt?: string | null;
  // Review status (derived from latest review)
  review_status?: ReviewStatus;
  latest_review?: LatestReview | null;
}

// Start review request/response
interface StartReviewRequest {
  model: string;
  max_items?: number;
}

interface StartReviewResponse {
  success: boolean;
  review_id: string;
  parent_job_run_id: string;
  job_type: string;
  reviewer_model: string;
  items_selected: number;
  batches_enqueued: number;
}

// Backend response shape from worker/lib/admin-config.js handleGetJobRunItems
interface JobItem {
  media_type: string;
  tmdb_id: number;
  job_type: string;
  status: string; // queued | processing | succeeded | failed | skipped
  attempt_count: number;
  locked_at: string | null;
  finished_at: string | null;
  error_code: string | null;
  error_message: string | null;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  output_schema_version: string | null;
  created_at: string;
  updated_at: string;
}

interface JobItemsResponse {
  items: JobItem[];
  next_cursor: { media_type: string; tmdb_id: number } | null;
}

// Fix failed request/response types
interface FixFailedRequest {
  dry_run: boolean;
  max_items: number;
  model?: string;
  prompt_version?: string;
  only_error_codes?: string[];
}

interface FixFailedPreviewItem {
  media_type: string;
  tmdb_id: number;
  error_code: string | null;
  error_message: string | null;
}

// Live progress response from GET /api/admin/jobs/:id/progress
interface JobProgress {
  job_run_id: string;
  items_attempted: number;
  counts: {
    queued: number;
    processing: number;
    succeeded: number;
    failed: number;
    skipped: number;
  };
  done: number;
  remaining: number;
  avg_seconds_per_item: number | null;
  eta_seconds: number | null;
  as_of: string;
}

interface FixFailedResponse {
  success: boolean;
  dry_run: boolean;
  parent_job_run_id: string;
  retry_job_run_id: string | null;
  job_type: string;
  model: string;
  prompt_version: string;
  items_selected: number;
  batches_enqueued: number;
  batch_size: number;
  next_cursor: { media_type: string; tmdb_id: number } | null;
  items?: FixFailedPreviewItem[];
  message?: string;
  tag_key?: string;
  tag_prompt?: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

// Derive UI status for job run from backend fields
type JobUiStatus = 'running' | 'has_errors' | 'complete';

function deriveJobStatus(job: JobRun): JobUiStatus {
  if (!job.finished_at) return 'running';
  if (job.items_failed > 0) return 'has_errors';
  return 'complete';
}

function getJobStatusIcon(status: JobUiStatus) {
  switch (status) {
    case 'complete':
      return <CheckCircle2 size={16} className="text-green-400" />;
    case 'has_errors':
      return <XCircle size={16} className="text-red-400" />;
    case 'running':
      return <Play size={16} className="text-blue-400" />;
    default:
      return <Clock size={16} className="text-slate-400" />;
  }
}

function getJobStatusColor(status: JobUiStatus) {
  switch (status) {
    case 'complete':
      return 'text-green-400';
    case 'has_errors':
      return 'text-red-400';
    case 'running':
      return 'text-blue-400';
    default:
      return 'text-slate-400';
  }
}

function getJobStatusLabel(status: JobUiStatus): string {
  switch (status) {
    case 'complete':
      return 'Complete';
    case 'has_errors':
      return 'Has Errors';
    case 'running':
      return 'Running';
    default:
      return 'Unknown';
  }
}

// Item status icons/colors (these match actual DB values)
function getItemStatusIcon(status: string) {
  switch (status) {
    case 'succeeded':
      return <CheckCircle2 size={14} className="text-green-400" />;
    case 'failed':
      return <XCircle size={14} className="text-red-400" />;
    case 'processing':
      return <Play size={14} className="text-blue-400" />;
    case 'skipped':
      return <Clock size={14} className="text-yellow-400" />;
    case 'queued':
    default:
      return <Clock size={14} className="text-slate-400" />;
  }
}

function getItemStatusColor(status: string) {
  switch (status) {
    case 'succeeded':
      return 'text-green-400';
    case 'failed':
      return 'text-red-400';
    case 'processing':
      return 'text-blue-400';
    case 'skipped':
      return 'text-yellow-400';
    case 'queued':
    default:
      return 'text-slate-400';
  }
}

function formatJobType(jobType: string): string {
  switch (jobType) {
    case 'ai_facets_v1':
      return 'AI Facets';
    case 'ai_overlay_genres_v1':
      return 'AI Overlay Genres';
    case 'ai_microtag_v1':
      return 'AI Microtag';
    default:
      return jobType;
  }
}

function formatDuration(startedAt: string, finishedAt: string | null): string {
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const durationMs = end - start;

  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${(durationMs / 3600000).toFixed(1)}h`;
}

// Review status badge helper
function getReviewStatusBadge(status: ReviewStatus): { label: string; className: string } {
  switch (status) {
    case 'none':
      return { label: 'Not Reviewed', className: 'bg-slate-500/20 text-slate-400' };
    case 'pending':
      return { label: 'Review Pending', className: 'bg-blue-500/20 text-blue-400' };
    case 'changes_suggested':
      return { label: 'Changes Suggested', className: 'bg-yellow-500/20 text-yellow-400' };
    case 'approved':
      return { label: 'Approved', className: 'bg-green-500/20 text-green-400' };
    case 'applied':
      return { label: 'Applied', className: 'bg-teal-500/20 text-teal-400' };
    case 'no_changes':
      return { label: 'No Changes', className: 'bg-slate-500/20 text-slate-300' };
    default:
      return { label: status, className: 'bg-slate-500/20 text-slate-400' };
  }
}

export function AdminJobRunDetailPage() {
  const { id: jobRunId } = useParams<{ id: string }>();
  const { session } = useAuth();
  const navigate = useNavigate();

  // Job run state
  const [job, setJob] = useState<JobRun | null>(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobError, setJobError] = useState<string | null>(null);

  // Items state
  const [statusFilter, setStatusFilter] = useState('');
  const [itemsResponse, setItemsResponse] = useState<JobItemsResponse | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<
    Array<{ media_type: string; tmdb_id: number }>
  >([]);
  const limit = 50;

  // Live progress state (for running jobs)
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);

  // Fix failed modal state
  const [showFixFailedModal, setShowFixFailedModal] = useState(false);
  const [fixFailedMaxItems, setFixFailedMaxItems] = useState(200);
  const [fixFailedModel, setFixFailedModel] = useState('');
  const [fixFailedPromptVersion, setFixFailedPromptVersion] = useState('');
  const [fixFailedDryRun, setFixFailedDryRun] = useState(true);
  const [fixFailedLoading, setFixFailedLoading] = useState(false);
  const [fixFailedResult, setFixFailedResult] = useState<FixFailedResponse | null>(null);
  const [fixFailedError, setFixFailedError] = useState<string | null>(null);

  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewModel, setReviewModel] = useState(ALLOWED_REVIEWER_MODELS[0].value);
  const [reviewMaxItems, setReviewMaxItems] = useState(500);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Fetch job detail
  const fetchJob = useCallback(async () => {
    if (!session?.access_token || !jobRunId) return;

    setJobLoading(true);
    setJobError(null);

    try {
      const data = await apiFetch<JobRun>(`/admin/jobs/${jobRunId}`, {
        token: session.access_token,
      });
      setJob(data);
    } catch (err) {
      setJobError(err instanceof Error ? err.message : 'Failed to fetch job');
    } finally {
      setJobLoading(false);
    }
  }, [session?.access_token, jobRunId]);

  // Fetch items
  const fetchItems = useCallback(
    async (cursor?: { media_type: string; tmdb_id: number }) => {
      if (!session?.access_token || !jobRunId) return;

      setItemsLoading(true);
      setItemsError(null);

      try {
        let url = `/admin/jobs/${jobRunId}/items?limit=${limit}`;
        if (statusFilter) url += `&status=${statusFilter}`;
        if (cursor) {
          url += `&cursor_media_type=${cursor.media_type}&cursor_tmdb_id=${cursor.tmdb_id}`;
        }

        const data = await apiFetch<JobItemsResponse>(url, {
          token: session.access_token,
        });
        setItemsResponse(data);
      } catch (err) {
        setItemsError(err instanceof Error ? err.message : 'Failed to fetch items');
      } finally {
        setItemsLoading(false);
      }
    },
    [session?.access_token, jobRunId, statusFilter]
  );

  // Fetch live progress (status counts, avg speed, ETA)
  const fetchProgress = useCallback(async () => {
    if (!session?.access_token || !jobRunId) return;

    try {
      const data = await apiFetch<JobProgress>(`/admin/jobs/${jobRunId}/progress`, {
        token: session.access_token,
      });
      setJobProgress(data);
    } catch (err) {
      // Silent fail for progress - not critical
      console.error('Failed to fetch progress:', err);
    }
  }, [session?.access_token, jobRunId]);

  // Initial fetch
  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  useEffect(() => {
    setCursorStack([]);
    fetchItems();
  }, [fetchItems]);

  // Initial progress fetch
  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  // Poll progress while job is running (every 10 seconds - 10M-scale safe)
  useEffect(() => {
    // Only poll if job is loaded and still running
    if (!job || job.finished_at) return;

    const interval = setInterval(() => {
      fetchProgress();
    }, 10_000);

    return () => clearInterval(interval);
  }, [job, fetchProgress]);

  const handleNextPage = () => {
    if (itemsResponse?.next_cursor) {
      if (itemsResponse.items.length > 0) {
        setCursorStack((prev) => [
          ...prev,
          {
            media_type: itemsResponse.items[0].media_type,
            tmdb_id: itemsResponse.items[0].tmdb_id,
          },
        ]);
      }
      fetchItems(itemsResponse.next_cursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorStack.length > 0) {
      const newStack = [...cursorStack];
      newStack.pop();
      setCursorStack(newStack);
      const prevCursor = newStack.length > 0 ? newStack[newStack.length - 1] : undefined;
      fetchItems(prevCursor);
    }
  };

  // Export failed items as JSON
  const handleExportFailed = async () => {
    if (!session?.access_token || !jobRunId) return;

    try {
      // Fetch all failed items (up to 1000)
      const data = await apiFetch<JobItemsResponse>(
        `/admin/jobs/${jobRunId}/items?status=failed&limit=1000`,
        { token: session.access_token }
      );

      // Backend returns { items, next_cursor }
      const blob = new Blob([JSON.stringify(data.items, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job-${jobRunId}-failed-items.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // Fix failed modal handlers
  const openFixFailedModal = () => {
    setShowFixFailedModal(true);
    setFixFailedMaxItems(200);
    setFixFailedModel('');
    setFixFailedPromptVersion('');
    setFixFailedDryRun(true);
    setFixFailedResult(null);
    setFixFailedError(null);
  };

  const closeFixFailedModal = () => {
    setShowFixFailedModal(false);
    setFixFailedResult(null);
    setFixFailedError(null);
  };

  const handleFixFailed = async () => {
    if (!session?.access_token || !jobRunId) return;

    setFixFailedLoading(true);
    setFixFailedError(null);
    setFixFailedResult(null);

    try {
      const body: FixFailedRequest = {
        dry_run: fixFailedDryRun,
        max_items: fixFailedMaxItems,
      };
      if (fixFailedModel.trim()) body.model = fixFailedModel.trim();
      if (fixFailedPromptVersion.trim()) body.prompt_version = fixFailedPromptVersion.trim();

      const result = await apiFetch<FixFailedResponse>(
        `/admin/jobs/${jobRunId}/fix-failed`,
        {
          method: 'POST',
          token: session.access_token,
          body,
        }
      );

      setFixFailedResult(result);

      // If not dry run and successful, navigate to new run
      if (!fixFailedDryRun && result.retry_job_run_id) {
        setTimeout(() => {
          navigate(`/admin/jobs/${result.retry_job_run_id}`);
        }, 1500);
      }
    } catch (err) {
      setFixFailedError(err instanceof Error ? err.message : 'Failed to fix failed items');
    } finally {
      setFixFailedLoading(false);
    }
  };

  // Review modal handlers
  const openReviewModal = () => {
    setShowReviewModal(true);
    setReviewModel(ALLOWED_REVIEWER_MODELS[0].value);
    setReviewMaxItems(500);
    setReviewError(null);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setReviewError(null);
  };

  const handleStartReview = async () => {
    if (!session?.access_token || !jobRunId) return;

    setReviewLoading(true);
    setReviewError(null);

    try {
      const body: StartReviewRequest = {
        model: reviewModel,
        max_items: reviewMaxItems,
      };

      const result = await apiFetch<StartReviewResponse>(
        `/admin/jobs/${jobRunId}/review/run`,
        {
          method: 'POST',
          token: session.access_token,
          body,
        }
      );

      // Success - navigate to the review detail page
      closeReviewModal();
      navigate(`/admin/reviews/${result.review_id}`);
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Failed to start review');
    } finally {
      setReviewLoading(false);
    }
  };

  if (jobLoading && !job) {
    return (
      <AdminLayout title="Job Run" description="Loading...">
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  if (jobError) {
    return (
      <AdminLayout title="Job Run" description="Error loading job">
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle size={16} />
            <span>{jobError}</span>
          </div>
        </div>
        <Link
          to="/admin/jobs"
          className="mt-4 inline-flex items-center gap-2 text-teal-400 hover:text-teal-300"
        >
          <ChevronLeft size={16} />
          Back to Jobs
        </Link>
      </AdminLayout>
    );
  }

  const jobUiStatus = job ? deriveJobStatus(job) : null;

  return (
    <AdminLayout
      title={job ? `Job Run: ${formatJobType(job.job_type)}` : 'Job Run'}
      description={job?.id}
    >
      {/* Back link */}
      <Link
        to="/admin/jobs"
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6"
      >
        <ChevronLeft size={16} />
        Back to Jobs
      </Link>

      {/* Job Summary */}
      {job && jobUiStatus && (
        <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08] mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-xs text-slate-400 mb-1">Status</p>
              <div className="flex items-center gap-2">
                {getJobStatusIcon(jobUiStatus)}
                <span className={`font-medium ${getJobStatusColor(jobUiStatus)}`}>
                  {getJobStatusLabel(jobUiStatus)}
                </span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Job Type</p>
              <p className="text-white font-medium">
                {formatJobType(job.job_type)}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Progress</p>
              <p className="text-white font-medium">
                {/* Use live progress if available, else fall back to job static counts */}
                <span className="text-green-400">
                  {jobProgress ? jobProgress.counts.succeeded : job.items_succeeded}
                </span>
                <span className="text-slate-500"> / </span>
                <span>{jobProgress ? jobProgress.items_attempted : job.items_attempted}</span>
                {(jobProgress ? jobProgress.counts.failed : job.items_failed) > 0 && (
                  <span className="text-red-400 ml-2">
                    ({jobProgress ? jobProgress.counts.failed : job.items_failed} failed)
                  </span>
                )}
              </p>
              {/* Show processing count if any are in-flight */}
              {jobProgress && jobProgress.counts.processing > 0 && (
                <p className="text-xs text-blue-400 mt-1">
                  {jobProgress.counts.processing} processing
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Duration</p>
              <p className="text-white font-medium">
                {formatDuration(job.started_at, job.finished_at)}
              </p>
            </div>
          </div>

          {/* Speed and ETA row - only show while job is running */}
          {!job.finished_at && jobProgress && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-400 mb-1">Speed</p>
                <p className="text-white font-medium">
                  {jobProgress.avg_seconds_per_item !== null
                    ? `${jobProgress.avg_seconds_per_item.toFixed(1)}s / item`
                    : 'Calculating...'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">ETA</p>
                <p className="text-white font-medium">
                  {jobProgress.eta_seconds !== null
                    ? jobProgress.eta_seconds < 60
                      ? `~${Math.round(jobProgress.eta_seconds)}s`
                      : jobProgress.eta_seconds < 3600
                        ? `~${Math.round(jobProgress.eta_seconds / 60)}m`
                        : `~${(jobProgress.eta_seconds / 3600).toFixed(1)}h`
                    : 'Calculating...'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Remaining</p>
                <p className="text-white font-medium">
                  {jobProgress.remaining} items
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Done</p>
                <p className="text-white font-medium">
                  {jobProgress.done} items
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-xs text-slate-400 mb-1">Started</p>
              <p className="text-slate-300 text-sm">
                {new Date(job.started_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Finished</p>
              <p className="text-slate-300 text-sm">
                {job.finished_at
                  ? new Date(job.finished_at).toLocaleString()
                  : 'In progress...'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Model</p>
              <p className="text-slate-300 text-sm">{job.model || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Prompt Version</p>
              <p className="text-slate-300 text-sm">{job.prompt_version || '-'}</p>
            </div>
          </div>

          {/* Microtag-specific info (job_type === 'ai_microtag_v1') */}
          {job.job_type === 'ai_microtag_v1' && (job.tag_key || job.tag_prompt) && (
            <div className="pt-4 border-t border-white/[0.08]">
              {job.tag_key && (
                <div className="mb-2">
                  <p className="text-xs text-slate-400 mb-1">Tag Key</p>
                  <code className="text-sm text-teal-400 bg-[#0F1419] px-2 py-1 rounded">
                    {job.tag_key}
                  </code>
                </div>
              )}
              {job.tag_prompt && (
                <div>
                  <p className="text-xs text-slate-400 mb-1">Tag Prompt</p>
                  <p className="text-sm text-slate-300 bg-[#0F1419] p-2 rounded">
                    {job.tag_prompt}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Review status section - only for finished jobs */}
          {job.finished_at && (
            <div className="pt-4 border-t border-white/[0.08]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <p className="text-xs text-slate-400">Review Status</p>
                  {job.review_status && (
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getReviewStatusBadge(job.review_status).className}`}>
                      {getReviewStatusBadge(job.review_status).label}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Link to latest review if exists */}
                  {job.latest_review && (
                    <Link
                      to={`/admin/reviews/${job.latest_review.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-teal-400 bg-teal-500/10 rounded-lg hover:bg-teal-500/20 transition-colors"
                    >
                      <ExternalLink size={14} />
                      View Review
                    </Link>
                  )}
                  {/* Start new review button */}
                  <button
                    onClick={openReviewModal}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-400 bg-purple-500/10 rounded-lg hover:bg-purple-500/20 transition-colors"
                  >
                    <ClipboardCheck size={14} />
                    {job.latest_review ? 'New Review' : 'Start Review'}
                  </button>
                </div>
              </div>
              {/* Latest review summary */}
              {job.latest_review && (
                <div className="mt-3 p-3 bg-[#0F1419] rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Latest Review</span>
                    <span className="text-slate-500 text-xs">
                      {new Date(job.latest_review.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
                    <div>
                      <span className="text-slate-500">Status:</span>{' '}
                      <span className="text-slate-300">
                        {job.latest_review.applied_at ? 'Applied' :
                         job.latest_review.approved_at ? 'Approved' :
                         !job.latest_review.finished_at ? 'Reviewing' :
                         job.latest_review.items_suggested > 0 ? 'Changes Suggested' : 'No Changes'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Items:</span>{' '}
                      <span className="text-slate-300">{job.latest_review.items_attempted}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Suggested:</span>{' '}
                      <span className="text-yellow-400">{job.latest_review.items_suggested}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Finished:</span>{' '}
                      <span className="text-teal-400">
                        {job.latest_review.finished_at ? 'Yes' : 'Running...'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error summary (job-level error) */}
          {job.error_summary && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-xs text-red-400 mb-1">Error Summary</p>
              <p className="text-sm text-red-300">{job.error_summary}</p>
            </div>
          )}

          {/* Selector (collapsed) */}
          <details className="mt-4">
            <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-300">
              View selector JSON
            </summary>
            <pre className="mt-2 text-xs text-slate-400 bg-[#0F1419] p-2 rounded overflow-x-auto">
              {JSON.stringify(job.selector, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {/* Items Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Items</h3>
          <div className="flex items-center gap-2">
            {/* Fix Failed button - only show for finished runs with errors */}
            {job && job.items_failed > 0 && job.finished_at && (
              <button
                onClick={openFixFailedModal}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-orange-400 bg-orange-500/10 rounded-lg hover:bg-orange-500/20 transition-colors"
                title="Retry failed items"
              >
                <Wrench size={14} />
                Fix Failed
              </button>
            )}
            {job && job.items_failed > 0 && (
              <button
                onClick={handleExportFailed}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-300 bg-[#1A1F26] rounded-lg hover:text-white transition-colors"
              >
                <Download size={14} />
                Export Failed
              </button>
            )}
            <button
              onClick={() => {
                setCursorStack([]);
                fetchItems();
                fetchProgress();
              }}
              disabled={itemsLoading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-300 bg-[#1A1F26] rounded-lg hover:text-white transition-colors"
            >
              <RefreshCw size={14} className={itemsLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2 mb-4">
          <Filter size={14} className="text-slate-400" />
          <div className="flex gap-1 flex-wrap">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  statusFilter === opt.value
                    ? 'bg-teal-500 text-white'
                    : 'bg-[#1A1F26] text-slate-400 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {itemsError && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle size={16} />
              <span>{itemsError}</span>
            </div>
          </div>
        )}

        {/* Items table */}
        {itemsResponse && (
          <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08]">
            {itemsResponse.items.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                No items found{statusFilter && ` with status "${statusFilter}"`}
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-400 border-b border-white/[0.08]">
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2 pr-4">TMDB ID</th>
                        <th className="pb-2 pr-4">Type</th>
                        <th className="pb-2 pr-4">Attempts</th>
                        <th className="pb-2 pr-4">Updated</th>
                        <th className="pb-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsResponse.items.map((item) => (
                        <tr
                          key={`${item.media_type}-${item.tmdb_id}`}
                          className="border-b border-white/[0.04]"
                        >
                          <td className="py-2 pr-4">
                            <div className="flex items-center gap-2">
                              {getItemStatusIcon(item.status)}
                              <span className={getItemStatusColor(item.status)}>
                                {item.status}
                              </span>
                            </div>
                          </td>
                          <td className="py-2 pr-4">
                            <a
                              href={`/${item.media_type}/${item.tmdb_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-teal-400 hover:text-teal-300"
                            >
                              {item.tmdb_id}
                            </a>
                          </td>
                          <td className="py-2 pr-4 text-slate-300">{item.media_type}</td>
                          <td className="py-2 pr-4 text-slate-400">
                            {item.attempt_count}
                          </td>
                          <td className="py-2 pr-4 text-slate-400 text-xs">
                            {new Date(item.updated_at).toLocaleString()}
                          </td>
                          <td className="py-2 max-w-xs">
                            {item.error_code || item.error_message ? (
                              <span
                                className="text-xs text-red-400 truncate block"
                                title={`${item.error_code || ''}: ${item.error_message || ''}`}
                              >
                                {item.error_code && (
                                  <code className="bg-red-500/10 px-1 rounded mr-1">
                                    {item.error_code}
                                  </code>
                                )}
                                {item.error_message && (
                                  <>
                                    {item.error_message.substring(0, 80)}
                                    {item.error_message.length > 80 && '...'}
                                  </>
                                )}
                              </span>
                            ) : (
                              <span className="text-slate-500 text-xs">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.08]">
                  <p className="text-sm text-slate-400">
                    Page {cursorStack.length + 1}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={cursorStack.length === 0 || itemsLoading}
                      className="px-3 py-1.5 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={!itemsResponse.next_cursor || itemsLoading}
                      className="px-3 py-1.5 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Fix Failed Modal */}
      {showFixFailedModal && job && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1A1F26] rounded-lg border border-white/[0.08] w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
              <h3 className="text-lg font-semibold text-white">
                Fix Failed Items
              </h3>
              <button
                onClick={closeFixFailedModal}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              {/* Job info */}
              <div className="text-sm text-slate-400">
                <p>Job: <span className="text-white">{formatJobType(job.job_type)}</span></p>
                <p>Failed items: <span className="text-red-400">{job.items_failed}</span></p>
              </div>

              {/* Form inputs */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Max Items</label>
                  <input
                    type="number"
                    value={fixFailedMaxItems}
                    onChange={(e) => setFixFailedMaxItems(Math.min(2000, Math.max(1, parseInt(e.target.value) || 1)))}
                    min={1}
                    max={2000}
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Model Override (optional)</label>
                  <select
                    value={fixFailedModel}
                    onChange={(e) => setFixFailedModel(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  >
                    {ALLOWED_TAGGING_MODELS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Prompt Version Override (optional)</label>
                  <input
                    type="text"
                    value={fixFailedPromptVersion}
                    onChange={(e) => setFixFailedPromptVersion(e.target.value)}
                    placeholder="e.g., v1-opus-retry"
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="dryRunDetail"
                    checked={fixFailedDryRun}
                    onChange={(e) => setFixFailedDryRun(e.target.checked)}
                    className="w-4 h-4 rounded bg-[#0F1419] border-white/[0.08] text-teal-500 focus:ring-teal-500"
                  />
                  <label htmlFor="dryRunDetail" className="text-sm text-slate-300">
                    Dry run (preview only)
                  </label>
                </div>
              </div>

              {/* Error display */}
              {fixFailedError && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertTriangle size={14} />
                    <span>{fixFailedError}</span>
                  </div>
                </div>
              )}

              {/* Result display */}
              {fixFailedResult && (
                <div className={`p-3 rounded-lg border ${
                  fixFailedResult.dry_run
                    ? 'bg-blue-500/10 border-blue-500/20'
                    : 'bg-green-500/10 border-green-500/20'
                }`}>
                  <p className={`text-sm font-medium ${
                    fixFailedResult.dry_run ? 'text-blue-400' : 'text-green-400'
                  }`}>
                    {fixFailedResult.dry_run ? 'Preview' : 'Retry Run Created'}
                  </p>
                  <p className="text-sm text-slate-300 mt-1">
                    Items selected: {fixFailedResult.items_selected}
                  </p>
                  {!fixFailedResult.dry_run && fixFailedResult.retry_job_run_id && (
                    <p className="text-sm text-slate-300">
                      Batches enqueued: {fixFailedResult.batches_enqueued}
                    </p>
                  )}
                  {fixFailedResult.next_cursor && (
                    <p className="text-xs text-slate-400 mt-1">
                      More items available (use cursor to continue)
                    </p>
                  )}
                  {fixFailedResult.items_selected === 0 && (
                    <p className="text-sm text-slate-400 mt-1">
                      {fixFailedResult.message || 'No failed items match the criteria'}
                    </p>
                  )}
                  {!fixFailedResult.dry_run && fixFailedResult.retry_job_run_id && (
                    <p className="text-xs text-teal-400 mt-2">
                      Redirecting to new run...
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t border-white/[0.08]">
              <button
                onClick={closeFixFailedModal}
                className="px-4 py-2 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleFixFailed}
                disabled={fixFailedLoading || (!fixFailedDryRun && !!fixFailedResult?.retry_job_run_id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  fixFailedDryRun
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {fixFailedLoading ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : fixFailedDryRun ? (
                  'Preview'
                ) : (
                  'Execute Retry'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && job && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1A1F26] rounded-lg border border-white/[0.08] w-full max-w-lg mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
              <h3 className="text-lg font-semibold text-white">
                Start Review
              </h3>
              <button
                onClick={closeReviewModal}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              {/* Job info */}
              <div className="text-sm text-slate-400">
                <p>Job: <span className="text-white">{formatJobType(job.job_type)}</span></p>
                <p>Items succeeded: <span className="text-green-400">{job.items_succeeded}</span></p>
              </div>

              {/* Form inputs */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Reviewer Model</label>
                  <select
                    value={reviewModel}
                    onChange={(e) => setReviewModel(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  >
                    {ALLOWED_REVIEWER_MODELS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Max Items (1-500)</label>
                  <input
                    type="number"
                    value={reviewMaxItems}
                    onChange={(e) => setReviewMaxItems(Math.min(500, Math.max(1, parseInt(e.target.value) || 1)))}
                    min={1}
                    max={500}
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>

              {/* Info box */}
              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <p className="text-xs text-purple-300">
                  Review will analyze the AI tagging output using a separate model.
                  Changes will be suggested but <strong>not applied automatically</strong>.
                  You must approve and apply changes manually.
                </p>
              </div>

              {/* Error display */}
              {reviewError && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertTriangle size={14} />
                    <span>{reviewError}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t border-white/[0.08]">
              <button
                onClick={closeReviewModal}
                className="px-4 py-2 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStartReview}
                disabled={reviewLoading}
                className="px-4 py-2 text-sm font-medium bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reviewLoading ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  'Start Review'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
